import OktaAuth, { EVENT_ERROR } from "@okta/okta-auth-js";
import { log } from "@jutro/logger";
import { joinUrl } from "@jutro/platform";
import { AuthErrorCodes } from "../../core/AuthErrorCodes";
import { checkDeprecatedAuthConfiguration } from "../../core/checkDeprecatedAuthConfiguration";
import { checkRequiredAuthConfiguration } from "../../core/checkRequiredAuthConfiguration";
import { getAuthConfig } from "../../core/getAuthConfig";
import { JUTRO_AUTH_POPUP_CONTEXT_NAME } from "../../utils/authPopupUtils";
import {
  restoreHistoryState,
  saveHistoryState,
} from "../../utils/historyState";
import { mergeParamsIfExists } from "../../utils/mergeParams";
import { getOktaConfiguration } from "./getOktaConfiguration";
export const getNativeOktaAuthClient = (basePath) => {
  "development" === process.env.NODE_ENV &&
    (checkRequiredAuthConfiguration(), checkDeprecatedAuthConfiguration());
  const oidc = new OktaAuth(getOktaConfiguration(basePath));
  let tokensChangedCallback, tokensErrorCallback;
  const removeListeners = () => {
      oidc.authStateManager.unsubscribe(tokensChangedCallback),
        oidc.tokenManager.off(EVENT_ERROR, tokensErrorCallback);
    },
    getTokens = () => {
      const {
        accessToken: { accessToken: accessToken } = { accessToken: void 0 },
        idToken: { idToken: idToken } = { idToken: void 0 },
      } = oidc.tokenManager.getTokensSync();
      return void 0 !== accessToken && void 0 !== idToken
        ? { accessToken: accessToken, idToken: idToken }
        : null;
    };
  return {
    start: () => oidc.start(),
    cleanup: () => (removeListeners(), Promise.resolve()),
    getTokens: getTokens,
    getOrRenewAccessToken: () =>
      oidc.getAccessToken()
        ? oidc.getOrRenewAccessToken()
        : Promise.resolve(null),
    getUserInfo: () => oidc.getUser(),
    async login(callbackPath, extraParams) {
      const { isPassiveTokenRenewalEnabled: isPassiveTokenRenewalEnabled } =
        getAuthConfig();
      if (!isPassiveTokenRenewalEnabled && (await oidc.session.exists())) {
        try {
          const { tokens: tokens } = await oidc.token.getWithoutPrompt();
          return void oidc.tokenManager.setTokens(tokens);
        } catch (error) {
          const currentLocation = window.location.href;
          const isProd = currentLocation.includes("fwsprod");

          const ssoBase = isProd
            ? "https://fwsia.okta.com/app/fwsia_guidewirecloud_1/exkwynw8voRWCDNMQ696/sso/saml?RelayState="
            : "https://fwsia.oktapreview.com/app/fwsia_guidewirecloud_1/exk3m7ez89hipdU371d7/sso/saml?RelayState=";

          window.location.href = `${ssoBase}${currentLocation}`;

          return false;
        }
      }
      const loginUri =
        callbackPath ||
        window.location.href.replace(window.location.origin, "");
      return (
        oidc.setOriginalUri(loginUri),
        saveHistoryState(),
        oidc.signInWithRedirect({ extraParams: extraParams })
      );
    },
    logout(fromPath, extraParams) {
      const postLogoutRedirectUri =
        fromPath && /^https?:\/\//.test(fromPath)
          ? fromPath
          : joinUrl(window.location.origin, fromPath);
      return oidc.signOut({ postLogoutRedirectUri: postLogoutRedirectUri });
    },
    async loginRedirect() {
      const callbackPath = oidc.getOriginalUri(),
        state = restoreHistoryState();
      return (
        await oidc.handleRedirect(),
        { callbackPath: callbackPath, state: state }
      );
    },
    async renewTokens() {
      const key = oidc.tokenManager.getStorageKeyByType("accessToken");
      await oidc.tokenManager.renew(key ?? "accessToken");
    },
    getRoutesConfiguration: () => ({ redirectUri: oidc.options.redirectUri }),
    async restoreSession() {},
    registerStateListeners(_ref) {
      let { onTokenChanged: onTokenChanged, onError: onError } = _ref;
      removeListeners(),
        (tokensChangedCallback = () => {
          onTokenChanged(getTokens());
        }),
        (tokensErrorCallback = (error) => {
          "login_required" === error.errorCode
            ? (log.error(
                "Silent login is enabled, however third-party cookies are disabled in your browser. Silent login requires third-party cookies in order to function. The recommended solution is to switch to refresh token rotation. To switch, add the `offline_access` scope to the `JUTRO_AUTH_SCOPE` environment variable and enable refresh token rotation in Keti. For more information, see the Jutro documentation: https://docs.guidewire.com/jutro/documentation/latest/auth-client#third-party-cookies",
              ),
              onError(AuthErrorCodes.JUTRO_AUTH_LOGIN_REQUIRED_ERROR, error))
            : onError(AuthErrorCodes.JUTRO_AUTH_REFRESH_ERROR, error);
        }),
        oidc.authStateManager.subscribe(tokensChangedCallback),
        oidc.tokenManager.on(EVENT_ERROR, tokensErrorCallback);
    },
    removeStateListeners() {
      removeListeners();
    },
    async updateTokensWithPopup() {
      let extraParams =
        arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : {};
      const { tokens: tokens } = await oidc.token.getWithPopup({
        extraParams: mergeParamsIfExists(
          "JUTRO_AUTH_LOGIN_QUERY_EXTRAS",
          extraParams,
        ),
        scopes: oidc.options.scopes,
        popupTitle: JUTRO_AUTH_POPUP_CONTEXT_NAME,
      });
      oidc.tokenManager.setTokens(tokens);
    },
    isSessionActive: async () => oidc.session.exists(),
    async updateTokensWithoutPrompt() {
      const { tokens: tokens } = await oidc.token.getWithoutPrompt();
      oidc.tokenManager.setTokens(tokens);
    },
    __simulateTokensRefreshError() {
      oidc.tokenManager.emitError(new Error("Simulated Refresh Error"));
    },
  };
};
